/**
 * Oxbo-M client — enterprise master data (well register + catalogs).
 *
 * Read-through cache posture: Oxbo-Z consults & caches, never writes. Base URL
 * is VITE_OXBO_M_URL (m.dev.oxbo.energy by default). In dev / standalone mode
 * MSW intercepts these calls and serves mock register + catalog data, so the
 * app runs with no live backend.
 *
 * Endpoints mirror the live contract exactly:
 *   GET /v1/wells                              — list the register
 *   GET /v1/wells/{well_id}                    — one well
 *   GET /v1/categories/{key}/resolved          — an effective-dated catalog
 */
import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import type { WellOut, ResolvedCatalogOut, WellView, CatalogResponse } from "./types";
import { toWellView } from "./mappers";
import { getDevToken } from "./devAuth";
import { getProdToken } from "./prodAuth";
import { catalogCapabilities } from "./capabilities";
import type { UserRole } from "@app/slices/prefsSlice";

/**
 * The Bearer token to attach to Oxbo-M calls. Prefers the real-login prod token
 * (VITE_PROD_AUTH build); falls back to the dev-minted token (VITE_DEV_AUTH
 * build). In a mocks-only build both are null and no Authorization header is
 * sent (MSW answers the call anyway).
 */
function getAuthToken(): string | null {
  return getProdToken() ?? getDevToken();
}

/** Server-side filters the M contract accepts on /v1/wells. */
export interface WellListParams {
  status?: string;
  active_only?: boolean;
}

const M_BASE_URL = import.meta.env.VITE_OXBO_M_URL ?? "https://m.dev.oxbo.energy";

export const mApi = createApi({
  reducerPath: "mApi",
  baseQuery: fetchBaseQuery({
    baseUrl: `${M_BASE_URL}/v1`,
    // Inject the current JWT (real prod-login token, or dev-minted token). In a
    // mocks-only build both are null and no Authorization header is sent.
    prepareHeaders: (headers, { getState }) => {
      const token = getAuthToken();
      if (token) headers.set("Authorization", `Bearer ${token}`);
      // Forward the user's catalog-manage capabilities so Oxbo-M can decide
      // whether to include a catalog's `admin` block (§22c). This mirrors the
      // live contract where the token's claims carry these; against MSW the
      // handler reads them from X-Oxbo-Caps. A field user sends none → the
      // server omits the admin URL, so it never reaches the client.
      const role = (getState() as { prefs?: { role?: UserRole } }).prefs?.role;
      const caps = catalogCapabilities(role ?? "field");
      if (caps.length > 0) headers.set("X-Oxbo-Caps", caps.join(","));
      return headers;
    },
  }),
  // Enterprise master data changes rarely — keep it cached generously.
  keepUnusedDataFor: 300,
  tagTypes: ["Well", "Catalog"],
  endpoints: (builder) => ({
    // Wells come back as raw WellOut[]; we normalize to WellView[] here so the
    // whole UI works in one clean, unit-aware shape.
    getWells: builder.query<WellView[], WellListParams | void>({
      query: (params) => ({ url: "/wells", params: params ?? { active_only: false } }),
      transformResponse: (raw: WellOut[]) => raw.map(toWellView),
      providesTags: (result) =>
        result
          ? [
              ...result.map((w) => ({ type: "Well" as const, id: w.id })),
              { type: "Well" as const, id: "LIST" },
            ]
          : [{ type: "Well" as const, id: "LIST" }],
    }),
    getWell: builder.query<WellView, string>({
      query: (id) => `/wells/${id}`,
      transformResponse: (raw: WellOut) => toWellView(raw),
      providesTags: (_r, _e, id) => [{ type: "Well", id }],
    }),
    // Resolved catalog — e.g. activity codes, cost classes, vendors. Pickers
    // in the report screens read these. `asOf` supports effective-dating.
    getResolvedCatalog: builder.query<
      ResolvedCatalogOut,
      { categoryKey: string; asOf?: string; siteRegionKey?: string }
    >({
      query: ({ categoryKey, asOf, siteRegionKey }) => ({
        url: `/categories/${categoryKey}/resolved`,
        params: {
          ...(asOf ? { as_of: asOf } : {}),
          ...(siteRegionKey ? { site_region_key: siteRegionKey } : {}),
        },
      }),
      providesTags: (_r, _e, { categoryKey }) => [{ type: "Catalog", id: categoryKey }],
    }),

    /**
     * Catalog value-API (§22 / §23) — the picker-facing contract. Returns the
     * typeahead value list plus an OPTIONAL `admin` envelope that the server
     * omits for non-authorised callers (§22c). Supports server-side typeahead
     * (`q=`) + scope filtering for large catalogs (cost_accounts ~2,615) and
     * effective-dating (`as_of`).
     */
    getCatalog: builder.query<
      CatalogResponse,
      { key: string; q?: string; asOf?: string; scope?: string; active?: boolean }
    >({
      query: ({ key, q, asOf, scope, active }) => ({
        url: `${M_BASE_URL}/api/m/catalogs/${key}`,
        params: {
          ...(q ? { q } : {}),
          ...(asOf ? { as_of: asOf } : {}),
          ...(scope ? { scope } : {}),
          ...(active != null ? { active } : {}),
        },
      }),
      providesTags: (_r, _e, { key }) => [{ type: "Catalog", id: `cat:${key}` }],
    }),
  }),
});

export const {
  useGetWellsQuery,
  useGetWellQuery,
  useGetResolvedCatalogQuery,
  useGetCatalogQuery,
} = mApi;
